import React, { useCallback, useMemo, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    GENERAL_LEDGER_FILTER_KEYS,
    GENERAL_LEDGER_FILTER_TREE,
    GeneralLedgerFilterTarget,
    getGeneralLedgerTypeNodeId,
    isGeneralLedgerFilterRole,
    readGeneralLedgerRoles,
    readGeneralLedgerTypeFromNodeId,
    readGeneralLedgerTypes,
} from "@common/helpers/accounting/general-ledger-filter";
import { pruneItemsByQuery } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { collectItemValues } from "@components/pageHeader/filters/FilterPickerList/filterPickerTree";
import { BaseFilterProps } from "../../lib";
import css from "../CategoryFilter/AggregatedCategoryFilter.module.scss";

const TYPE_ITEMS: Array<PickerItemData<string>> =
    GENERAL_LEDGER_FILTER_TREE.map((node) => ({
        value: getGeneralLedgerTypeNodeId(node.type),
        label: node.type,
        children:
            node.purposes.length > 0
                ? node.purposes.map((purpose) => ({
                      value: purpose.role,
                      label: purpose.label,
                      tooltip: purpose.tooltip,
                  }))
                : undefined,
    }));

interface GeneralLedgerFilterProps extends BaseFilterProps {
    target: GeneralLedgerFilterTarget;
}

/** Splits picker values into the filter's two keys: types (`type:` ids) and purpose roles. */
const readSelection = (values: readonly string[]) => ({
    types: values.flatMap((value): AccountType[] => {
        const type = readGeneralLedgerTypeFromNodeId(value);
        return type === undefined ? [] : [type];
    }),
    roles: values.filter(isGeneralLedgerFilterRole),
});

export const GeneralLedgerFilter: React.FC<GeneralLedgerFilterProps> = ({
    filters,
    onChange,
    target,
}) => {
    const [query, setQuery] = useState("");
    const keys = GENERAL_LEDGER_FILTER_KEYS[target];

    const selectedTypes = useMemo(
        () => readGeneralLedgerTypes(filters[keys.types]),
        [filters, keys.types],
    );
    const selectedRoles = useMemo(
        () => readGeneralLedgerRoles(filters[keys.roles]),
        [filters, keys.roles],
    );
    const selectedValues = useMemo(
        () => [
            ...selectedTypes.map(getGeneralLedgerTypeNodeId),
            ...selectedRoles,
        ],
        [selectedRoles, selectedTypes],
    );

    const applySelection = useCallback(
        (values: readonly string[]) => {
            const { types, roles } = readSelection(values);
            onChange({
                [keys.types]: types.length > 0 ? types : undefined,
                [keys.roles]: roles.length > 0 ? roles : undefined,
            });
        },
        [keys.roles, keys.types, onChange],
    );

    const reverseSelection = useCallback(() => {
        const selectedSet = new Set(selectedValues);
        applySelection(
            collectItemValues(pruneItemsByQuery(TYPE_ITEMS, query)).filter(
                (value) => !selectedSet.has(value),
            ),
        );
    }, [applySelection, query, selectedValues]);

    return (
        <div className={css.filter}>
            <FilterPickerList
                items={TYPE_ITEMS}
                value={selectedValues}
                onValueChange={applySelection}
                onInputValueChange={setQuery}
                emptyLabel="No matching accounts"
                onReverse={reverseSelection}
            />
        </div>
    );
};
